import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hotKeywords } from '@/mocks/activity';

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
  initialKeyword?: string;
}

export default function SearchOverlay({ open, onClose, initialKeyword = '' }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState(initialKeyword);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setKeyword(initialKeyword);
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, initialKeyword]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = (value: string) => {
    const kw = value.trim();
    if (!kw) return;
    onClose();
    navigate(`/search?q=${encodeURIComponent(kw)}`);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-24 md:pt-32">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-2xl bg-background-50 rounded-lg border border-background-200 shadow-sm p-5 md:p-6 animate-fade-up">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(keyword);
          }}
          className="flex items-center gap-3"
        >
          <span className="w-9 h-9 flex items-center justify-center text-foreground-400">
            <i className="ri-search-line text-xl" />
          </span>
          <input
            ref={inputRef}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索话题、关键词或作者…"
            className="flex-1 bg-transparent text-base text-foreground-900 placeholder:text-foreground-400 outline-none py-2"
          />
          <button
            type="submit"
            className="rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-4 py-2 whitespace-nowrap transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>

        <div className="mt-5 pt-4 border-t border-background-200">
          <p className="text-xs font-label font-bold text-foreground-400 mb-2.5">热门搜索</p>
          <div className="flex flex-wrap gap-2">
            {hotKeywords.map((kw) => (
              <button
                key={kw}
                type="button"
                onClick={() => submit(kw)}
                className="rounded-full border border-background-300 bg-background-100 px-3 py-1.5 text-xs text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {kw}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}